<script lang="ts">
  import { f7 } from 'framework7-svelte';
  import { onMount, onDestroy } from 'svelte';
  import { globalLoaderStore } from './globalLoaderStore';
  
  let isVisible = false;
  let text = 'Loading...';
  let overlay = true;
  let spinner = true;
  let progress = 0;
  
  // Subscribe to the store
  const unsubscribe = globalLoaderStore.subscribe((state) => {
    isVisible = state.isVisible;
    text = state.text;
    overlay = state.overlay;
    spinner = state.spinner;
    progress = state.progress;
  });
  
  onDestroy(() => {
    unsubscribe();
  });
  
  // Helper functions to show/hide loader
  export function showLoader(options: {
    text?: string;
    overlay?: boolean;
    spinner?: boolean;
  } = {}) {
    globalLoaderStore.set({
      isVisible: true,
      text: options.text || 'Loading...',
      overlay: options.overlay !== false,
      spinner: options.spinner !== false,
      progress: 0
    });
  }
  
  export function hideLoader() {
    globalLoaderStore.set({
      isVisible: false,
      text: 'Loading...',
      overlay: true,
      spinner: true,
      progress: 0
    });
  }
  
  export function setProgress(value: number) {
    globalLoaderStore.update(state => ({
      ...state,
      progress: Math.min(Math.max(value, 0), 100)
    }));
  }
  
  // Expose functions globally for easy access
  if (typeof window !== 'undefined') {
    window.showGlobalLoader = showLoader;
    window.hideGlobalLoader = hideLoader;
    window.setGlobalLoaderProgress = setProgress;
  }
</script>

{#if isVisible}
  <!-- Angle-grinder style progress bar at top of page -->
  <div class="loading-bar" class:with-overlay={overlay}>
    <div class="bar" style="width: {progress || 50}%">
      <div class="peg"></div>
    </div>
  </div>
  
  <!-- Debug info -->
  <!-- <div style="position: fixed; top: 25px; left: 10px; background: white; padding: 5px; border: 1px solid #ccc; z-index: 10003; font-size: 12px;">
    Progress: {progress || 50}% | Visible: {isVisible} | Text: {text}
  </div> -->
  
  <!-- Optional spinner and text overlay -->
   <!--  turn off for now -->
    {#if false}   
    <div class="loading-overlay" class:with-overlay={overlay}>
      <div class="loading-content">
        {#if spinner}
          <div class="loading-spinner">
            <div class="spinner-icon"></div>
          </div>
        {/if}
        {#if text}
          <div class="loading-text">{text}</div>
        {/if}
      </div>
    </div>
  {/if}
{/if}

<style>
  /* Angle-grinder style loading bar */
  .loading-bar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    z-index: 10002;
    pointer-events: none;
    transition: opacity 350ms linear;
  }
  
  .loading-bar .bar {
    background: #29d;
    position: relative;
    height: 100%;
    width: 0%;
    transition: width 350ms ease;
    border-bottom-right-radius: 1px;
    border-top-right-radius: 1px;
  }
  
  /* Fancy blur effect from angle-grinder */
  .loading-bar .peg {
    position: absolute;
    width: 70px;
    right: 0;
    top: 0;
    height: 2px;
    opacity: 0.45;
    box-shadow: #29d 1px 0 6px 1px;
    border-radius: 100%;
  }
  
  /* Optional overlay for spinner and text */
  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 10001 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }
  
  .loading-overlay.with-overlay {
    background-color: rgba(0, 0, 0, 0.5);
    pointer-events: auto;
  }
  
  .loading-content {
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 20px 30px;
    border-radius: 8px;
    text-align: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
  }
  
  .loading-spinner {
    margin-bottom: 10px;
  }
  
  .spinner-icon {
    width: 14px;
    height: 14px;
    border: solid 2px transparent;
    border-top-color: #29d;
    border-left-color: #29d;
    border-radius: 50%;
    animation: loading-bar-spinner 400ms linear infinite;
    margin: 0 auto;
  }
  
  .loading-text {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
  }
  
  @keyframes loading-bar-spinner {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  /* Framework7 theme compatibility */
  :global(.theme-aurora) .loading-content {
    background-color: rgba(0, 0, 0, 0.9);
  }
  
  :global(.theme-ios) .loading-content {
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 12px;
  }
  
  :global(.theme-md) .loading-content {
    background-color: rgba(0, 0, 0, 0.8);
    border-radius: 4px;
  }
</style>
